import React from 'react';
import {observer} from "mobx-react";
import StatementsTable from "./StatementsTable";
import {ReadStatementDto} from "../../../model/statement/ReadStatementDto";
import {selectedCompanyStore} from "../../../store/SelectedCompany";
import {statementApi} from "../../../backend/StatementApi";
import LoaderWithCompanySelected from "../../../components/LoaderWithCompanySelected";
import {GroupStatementsBy, uiStateStore} from "../../../store/UiState";
import {Accordion, AccordionDetails, AccordionSummary, Tab, Tabs, Typography} from "@mui/material";
import NoSimIcon from '@mui/icons-material/NoSim';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DateRangeIcon from '@mui/icons-material/DateRange';
import {companyAccountsStore} from "../../../store/CompanyAccounts";
import TwoSidedPage from "../../../components/TwoSidedPage";
import {ActionMap} from "../../../utils/ActionMap";
import {Consumer} from "../../../utils/Types";
import {Toggle} from "../../../components/input/Toggle";

interface IProps {
}

enum FILTERS {
    ARCHIVED = "ARCHIVED",
    OPEN = "OPEN"
}

interface IState {
    statements: ReadStatementDto[],
    loading: boolean,
    selectedFileName: string,
    showUploadError: boolean,
    searchFor: FILTERS
}

const STATEMENT_REQUEST = new ActionMap<FILTERS, [], Promise<ReadStatementDto[]>>()
        .on(FILTERS.ARCHIVED).do(() => statementApi.fetchWithArchivedStatements())
        .on(FILTERS.OPEN).do(() => statementApi.fetchStatements())
        .selector()

const STATEMENT_FILTER_CHANGE = new ActionMap<FILTERS, [Consumer<FILTERS>]>()
        .on(FILTERS.ARCHIVED).do(changeFilter => changeFilter(FILTERS.OPEN))
        .on(FILTERS.OPEN).do(changeFilter => changeFilter(FILTERS.ARCHIVED))
        .selector()


@observer
class StatementsTableTab extends React.Component<IProps, IState> {

    constructor(props: IProps) {
        super(props);
        this.state = {
            statements: [],
            searchFor: FILTERS.OPEN,
            loading: true,
            selectedFileName: '',
            showUploadError: false
        };
    }

    async componentDidMount() {
        await companyAccountsStore.refresh();
        await this.refreshStatementsTable();
    }

    private refreshStatementsTable = async () => {
        this.setState({
            loading: true,
        })
        let statements: ReadStatementDto[];
        if (selectedCompanyStore.isSelected) {
            statements = await STATEMENT_REQUEST.for(this.state.searchFor).execute();
        } else {
            statements = [];
        }
        this.setState({
            loading: false,
            statements: statements
        })
    }

    render() {
        const {statements, loading} = this.state;
        return (
                <LoaderWithCompanySelected loading={loading}>
                    <TwoSidedPage
                            leftPanel={this.renderGroupBySelector()}
                            rightPanel={this.renderTable(statements, uiStateStore.statementTabGroupBy)}
                    />
                </LoaderWithCompanySelected>
        );
    }

    private renderGroupBySelector = () => {
        return (
                <React.Fragment>
                    <Tabs
                            value={uiStateStore.statementTabGroupBy}
                            onChange={this.groupByChanged}
                            orientation="vertical"
                            indicatorColor="primary"
                            textColor="primary"
                            aria-label="icon label tabs example"
                    >
                        <Tab style={{minWidth: 50}} icon={<NoSimIcon/>} value={GroupStatementsBy.NONE}
                             label="Don't group"/>
                        <Tab style={{minWidth: 50}} icon={<AccountBalanceIcon/>} value={GroupStatementsBy.IBAN}
                             label="IBAN"/>
                        <Tab style={{minWidth: 50}} icon={<DateRangeIcon/>} value={GroupStatementsBy.MONTH}
                             label="Month"/>
                    </Tabs>
                    <hr/>
                    <Toggle
                            labelPlacement="bottom"
                            checked={this.state.searchFor === FILTERS.ARCHIVED}
                            onChange={this.changeFilter}
                    >
                        Search Archived
                    </Toggle>
                </React.Fragment>
        );
    }

    private renderTable(statements: ReadStatementDto[], groupBy: GroupStatementsBy) {
        if (groupBy === GroupStatementsBy.NONE) {
            return <StatementsTable statements={statements} statementsChanged={this.refreshStatementsTable}/>
        } else {
            const grouped = this.doGroupStatements(statements, groupBy)
            return <>
                {
                    grouped.map((value) => {
                        const eventKey = value.key;
                        return (
                                <Accordion key={eventKey} square
                                           expanded={uiStateStore.statementTabSelectedGroup === eventKey}
                                           onChange={this.selectedSectionChanged(eventKey)}>
                                    <AccordionSummary>
                                        <Typography>{value.header}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <StatementsTable
                                                statements={value.statements}
                                                statementsChanged={this.refreshStatementsTable}
                                        />
                                    </AccordionDetails>
                                </Accordion>
                        );
                    })
                }
            </>
        }
    }

    private selectedSectionChanged = (selectedKey: any) => (event: React.ChangeEvent<{}>, newExpanded: boolean) => {
        uiStateStore.statementTabSelectedGroup = newExpanded ? selectedKey : '';
    }

    private doGroupStatements(statements: ReadStatementDto[], groupBy: GroupStatementsBy.IBAN | GroupStatementsBy.MONTH) {
        const groupFunction = groupBy === GroupStatementsBy.IBAN ?
                (row: ReadStatementDto) => {
                    const bank = companyAccountsStore.findByIban(row.iban, row.currency);
                    return `${bank?.iban} - ${bank?.name}`;
                } :
                (row: ReadStatementDto) => {
                    const month = row.periodFrom.getMonth() + 1;
                    return row.periodFrom.getFullYear() + "-" + (month > 9 ? month : "0" + month);
                }

        const grouped = this.groupBy(statements, groupFunction)

        const orderedKeys = Array.from(grouped.keys()).sort((one, two) => (one > two ? -1 : 1))
        return orderedKeys.map((key) => {
            const values = grouped.get(key)!!
            const header = key + " (" + values.length + " statements)";
            return {
                key: key,
                header: header,
                statements: values
            }
        })
    }

    private groupBy(statements: ReadStatementDto[], keyGetter: (row: ReadStatementDto) => string) {
        const map = new Map<string, ReadStatementDto[]>();
        statements.forEach((item) => {
            const key = keyGetter(item);
            const collection = map.get(key);
            if (!collection) {
                map.set(key, [item]);
            } else {
                collection.push(item);
            }
        });
        return map;
    }

    private groupByChanged = (event: any, value: GroupStatementsBy) => {
        uiStateStore.statementTabGroupBy = value;
    }

    private changeFilter = () => {
        STATEMENT_FILTER_CHANGE.for(this.state.searchFor)
                .execute(searchFor => this.setState({searchFor}, this.refreshStatementsTable))
    }
}

export default StatementsTableTab;